// src/pages/PrivacyPage.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';

export default function PrivacyPage() {
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="privacy-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
      />

      <div className="page-hero">
        <div className="container">
          <Shield size={48} className="hero-icon" />
          <h1>Gizlilik Politikası</h1>
          <p>Kişisel verilerinizin korunması bizim için önceliklidir</p>
          <div className="update-date">Son Güncelleme: 15 Aralık 2024</div>
        </div>
      </div>

      <div className="page-content">
        <div className="container">
          <div className="content-layout">
            {/* Sidebar Navigation */}
            <aside className="sidebar">
              <nav className="sticky-nav">
                <h3>İçindekiler</h3>
                <ul>
                  <li><a href="#introduction">Giriş</a></li>
                  <li><a href="#data-collection">Toplanan Veriler</a></li>
                  <li><a href="#data-usage">Verilerin Kullanımı</a></li>
                  <li><a href="#data-sharing">Veri Paylaşımı</a></li>
                  <li><a href="#data-security">Veri Güvenliği</a></li>
                  <li><a href="#cookies">Çerezler</a></li>
                  <li><a href="#user-rights">Kullanıcı Hakları</a></li>
                  <li><a href="#children">Çocukların Gizliliği</a></li>
                  <li><a href="#changes">Değişiklikler</a></li>
                  <li><a href="#contact">İletişim</a></li>
                </ul>
              </nav>
            </aside>

            {/* Main Content */}
            <main className="main-content">
              <section id="introduction" className="content-section">
                <h2>1. Giriş</h2>
                <div className="content-box">
                  <p>
                    Tronapazarlama olarak, müşterilerimizin kişisel verilerinin gizliliğini korumayı 
                    en önemli önceliklerimizden biri olarak kabul ediyoruz. Bu Gizlilik Politikası, 
                    web sitemizi ziyaret ettiğinizde veya hizmetlerimizi kullandığınızda kişisel 
                    bilgilerinizi nasıl topladığımızı, kullandığımızı ve koruduğumuzu açıklar.
                  </p>
                  <p>
                    Bu politika, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) ve ilgili 
                    mevzuat uyarınca hazırlanmıştır.
                  </p>
                </div>
              </section>

              <section id="data-collection" className="content-section">
                <h2>2. Toplanan Kişisel Veriler</h2>
                <div className="content-box">
                  <h3>2.1. Doğrudan Topladığımız Veriler</h3>
                  <div className="data-categories">
                    <div className="category-card">
                      <UserCheck size={24} />
                      <h4>Kimlik Bilgileri</h4>
                      <ul>
                        <li>Ad, soyad</li>
                        <li>Doğum tarihi</li>
                        <li>TC kimlik numarası (fatura için)</li>
                      </ul>
                    </div>
                    <div className="category-card">
                      <Mail size={24} />
                      <h4>İletişim Bilgileri</h4>
                      <ul>
                        <li>E-posta adresi</li>
                        <li>Telefon numarası</li>
                        <li>Adres bilgileri</li>
                      </ul>
                    </div>
                    <div className="category-card">
                      <Database size={24} />
                      <h4>İşlem Bilgileri</h4>
                      <ul>
                        <li>Sipariş geçmişi</li>
                        <li>Ödeme bilgileri</li>
                        <li>Sepet içeriği</li>
                      </ul>
                    </div>
                  </div>

                  <h3>2.2. Otomatik Toplanan Veriler</h3>
                  <ul className="info-list">
                    <li>IP adresi ve tarayıcı bilgileri</li>
                    <li>Cihaz türü ve işletim sistemi</li>
                    <li>Ziyaret edilen sayfalar ve tıklama verileri</li>
                    <li>Oturum süresi ve etkileşim verileri</li>
                    <li>Çerez bilgileri</li>
                  </ul>
                </div>
              </section>

              <section id="data-usage" className="content-section">
                <h2>3. Verilerin Kullanım Amaçları</h2>
                <div className="content-box">
                  <p>Topladığımız kişisel verilerinizi aşağıdaki amaçlarla kullanmaktayız:</p>
                  
                  <div className="usage-grid">
                    <div className="usage-item">
                      <div className="usage-number">1</div>
                      <div>
                        <h4>Sipariş İşlemleri</h4>
                        <p>Siparişlerinizi almak, işlemek ve teslim etmek</p>
                      </div>
                    </div>
                    <div className="usage-item">
                      <div className="usage-number">2</div>
                      <div>
                        <h4>Müşteri Hizmetleri</h4>
                        <p>Sorularınızı cevaplamak ve destek sağlamak</p>
                      </div>
                    </div>
                    <div className="usage-item">
                      <div className="usage-number">3</div>
                      <div>
                        <h4>Hesap Yönetimi</h4>
                        <p>Kullanıcı hesabınızı oluşturmak ve yönetmek</p>
                      </div>
                    </div>
                    <div className="usage-item">
                      <div className="usage-number">4</div>
                      <div>
                        <h4>Pazarlama</h4>
                        <p>Kampanya ve özel teklifler hakkında bilgilendirme</p>
                      </div>
                    </div>
                    <div className="usage-item">
                      <div className="usage-number">5</div>
                      <div>
                        <h4>Güvenlik</h4>
                        <p>Dolandırıcılığı önlemek ve sistemi korumak</p>
                      </div>
                    </div>
                    <div className="usage-item">
                      <div className="usage-number">6</div>
                      <div>
                        <h4>Yasal Yükümlülükler</h4>
                        <p>Kanuni gereklilikleri yerine getirmek</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section id="data-sharing" className="content-section">
                <h2>4. Veri Paylaşımı ve Aktarımı</h2>
                <div className="content-box">
                  <p>
                    Kişisel verilerinizi üçüncü şahıslarla paylaşmayız. Ancak aşağıdaki durumlarda 
                    sınırlı veri paylaşımı yapılabilir:
                  </p>
                  
                  <div className="sharing-list">
                    <div className="sharing-item">
                      <h4>✓ Kargo Firmaları</h4>
                      <p>Siparişlerinizi teslim etmek için adres ve iletişim bilgileriniz paylaşılır.</p>
                    </div>
                    <div className="sharing-item">
                      <h4>✓ Ödeme Kuruluşları</h4>
                      <p>Ödemeleri işlemek için güvenli ödeme sağlayıcılarıyla çalışırız.</p>
                    </div>
                    <div className="sharing-item">
                      <h4>✓ Yasal Makamlar</h4>
                      <p>Yasal zorunluluk halinde yetkili kurumlara bilgi sağlanabilir.</p>
                    </div>
                    <div className="sharing-item">
                      <h4>✓ Hizmet Sağlayıcılar</h4>
                      <p>Teknik destek ve altyapı hizmetleri için güvenilir iş ortaklarıyla çalışırız.</p>
                    </div>
                  </div>

                  <div className="important-note">
                    <Shield size={20} />
                    <p>
                      <strong>Önemli:</strong> Tüm üçüncü taraflarla gizlilik sözleşmeleri imzalanmıştır 
                      ve verileriniz sadece belirtilen amaçlar doğrultusunda kullanılır.
                    </p>
                  </div>
                </div>
              </section>

              <section id="data-security" className="content-section">
                <h2>5. Veri Güvenliği</h2>
                <div className="content-box">
                  <p>
                    Kişisel verilerinizin güvenliğini sağlamak için endüstri standardı güvenlik 
                    önlemleri uyguluyoruz:
                  </p>
                  
                  <div className="security-measures">
                    <div className="measure">
                      <Lock size={24} />
                      <h4>SSL/TLS Şifreleme</h4>
                      <p>256-bit SSL sertifikası ile tüm veri iletimi şifrelenir</p>
                    </div>
                    <div className="measure">
                      <Shield size={24} />
                      <h4>Güvenlik Duvarı</h4>
                      <p>Yetkisiz erişimlere karşı çok katmanlı koruma</p>
                    </div>
                    <div className="measure">
                      <Database size={24} />
                      <h4>Veri Yedekleme</h4>
                      <p>Düzenli yedekleme ile veri kaybı önleme</p>
                    </div>
                    <div className="measure">
                      <Eye size={24} />
                      <h4>Erişim Kontrolü</h4>
                      <p>Sınırlı personel erişimi ve iki faktörlü doğrulama</p>
                    </div>
                  </div>
                </div>
              </section>

              <section id="cookies" className="content-section">
                <h2>6. Çerezler (Cookies)</h2>
                <div className="content-box">
                  <p>
                    Web sitemizde kullanıcı deneyimini iyileştirmek için çerezler kullanıyoruz. 
                    Çerezler hakkında detaylı bilgi için <a href="/cookies">Çerez Politikası</a> 
                    sayfamızı ziyaret edebilirsiniz.
                  </p>
                  <ul className="info-list">
                    <li><strong>Zorunlu Çerezler:</strong> Sitenin çalışması için gereklidir</li>
                    <li><strong>Performans Çerezleri:</strong> Site performansını ölçer</li>
                    <li><strong>İşlevsellik Çerezleri:</strong> Tercihlerinizi hatırlar</li>
                    <li><strong>Pazarlama Çerezleri:</strong> Kişiselleştirilmiş reklamlar gösterir</li>
                  </ul>
                </div>
              </section>

              <section id="user-rights" className="content-section">
                <h2>7. Kullanıcı Hakları (KVKK)</h2>
                <div className="content-box">
                  <p>
                    6698 sayılı KVKK'nın 11. maddesi uyarınca aşağıdaki haklara sahipsiniz:
                  </p>
                  
                  <div className="rights-grid">
                    <div className="right-card">
                      <h4>📋 Bilgi Talep Etme</h4>
                      <p>Kişisel verilerinizin işlenip işlenmediğini öğrenme</p>
                    </div>
                    <div className="right-card">
                      <h4>🔍 Erişim Hakkı</h4>
                      <p>İşlenen kişisel verileriniz hakkında bilgi talep etme</p>
                    </div>
                    <div className="right-card">
                      <h4>✏️ Düzeltme Hakkı</h4>
                      <p>Eksik veya yanlış verilerin düzeltilmesini isteme</p>
                    </div>
                    <div className="right-card">
                      <h4>🗑️ Silme Hakkı</h4>
                      <p>Belirli koşullarda verilerin silinmesini talep etme</p>
                    </div>
                    <div className="right-card">
                      <h4>⛔ İtiraz Hakkı</h4>
                      <p>Verilerinizin işlenmesine itiraz etme</p>
                    </div>
                    <div className="right-card">
                      <h4>📤 Taşınabilirlik</h4>
                      <p>Verilerinizi başka bir veri sorumlusuna aktarma</p>
                    </div>
                  </div>

                  <div className="rights-procedure">
                    <h4>Haklarınızı Nasıl Kullanabilirsiniz?</h4>
                    <p>
                      Yukarıdaki haklarınızı kullanmak için <a href="mailto:kvkk@tronapazarlama.com">kvkk@tronapazarlama.com</a> 
                      adresine yazılı olarak başvurabilirsiniz. Başvurunuz 30 gün içinde değerlendirilecektir.
                    </p>
                  </div>
                </div>
              </section>

              <section id="children" className="content-section">
                <h2>8. Çocukların Gizliliği</h2>
                <div className="content-box">
                  <p>
                    Web sitemiz 18 yaş altı çocuklara yönelik değildir. Bilerek 18 yaş altı 
                    bireylerden kişisel veri toplamıyoruz. Eğer 18 yaşından küçükseniz, lütfen 
                    veli veya vasinizin izni ile sitemizi kullanın.
                  </p>
                  <p>
                    18 yaş altı bir çocuğun kişisel verilerini topladığımızı fark ederseniz, 
                    lütfen bizimle iletişime geçin. Bu verileri derhal sileceğiz.
                  </p>
                </div>
              </section>

              <section id="changes" className="content-section">
                <h2>9. Politika Değişiklikleri</h2>
                <div className="content-box">
                  <p>
                    Bu Gizlilik Politikası'nı zaman zaman güncelleyebiliriz. Önemli değişiklikler 
                    yapıldığında, sizi e-posta veya web sitemiz üzerinden bilgilendireceğiz.
                  </p>
                  <p>
                    Politikayı düzenli olarak gözden geçirmenizi öneririz. Bu sayfanın başında 
                    "Son Güncelleme" tarihini bulabilirsiniz.
                  </p>
                </div>
              </section>

              <section id="contact" className="content-section">
                <h2>10. İletişim</h2>
                <div className="content-box">
                  <p>
                    Gizlilik Politikamız hakkında sorularınız veya endişeleriniz varsa, 
                    bizimle iletişime geçmekten çekinmeyin:
                  </p>
                  
                  <div className="contact-info">
                    <div className="contact-item">
                      <Mail size={20} />
                      <div>
                        <strong>E-posta:</strong>
                        <a href="mailto:kvkk@tronapazarlama.com">kvkk@tronapazarlama.com</a>
                      </div>
                    </div>
                    <div className="contact-item">
                      <Shield size={20} />
                      <div>
                        <strong>KVKK Sorumlusu:</strong>
                        <p>Tronapazarlama KVKK Birimi</p>
                      </div>
                    </div>
                    <div className="contact-item">
                      <Database size={20} />
                      <div>
                        <strong>Adres:</strong>
                        <p>Antalya, Türkiye</p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </main>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Style() {
  return (
    <style>{`
      * { margin: 0; padding: 0; box-sizing: border-box; }
      
      .privacy-page {
        min-height: 100vh;
        background: #f8f9fa;
      }

      .container {
        max-width: 1320px;
        margin: 0 auto;
        padding: 0 20px;
      }

      .page-hero {
        background: linear-gradient(135deg, #2B2B2B 0%, #1a1a1a 100%);
        padding: 80px 0;
        text-align: center;
        color: #fff;
      }

      .hero-icon {
        margin-bottom: 24px;
        opacity: 0.9;
      }

      .page-hero h1 {
        font-size: 48px;
        font-weight: 800;
        margin-bottom: 16px;
      }

      .page-hero p {
        font-size: 18px;
        opacity: 0.8;
        margin-bottom: 16px;
      }

      .update-date {
        display: inline-block;
        background: rgba(255,255,255,0.2);
        padding: 8px 16px;
        border-radius: 8px;
        font-size: 14px;
      }

      .page-content {
        padding: 60px 0;
      }

      .content-layout {
        display: grid;
        grid-template-columns: 280px 1fr;
        gap: 40px;
        align-items: start;
      }

      /* Sidebar */
      .sidebar {
        position: relative;
      }

      .sticky-nav {
        position: sticky;
        top: 100px;
        background: #fff;
        padding: 24px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .sticky-nav h3 {
        font-size: 18px;
        margin-bottom: 16px;
        color: #2B2B2B;
      }

      .sticky-nav ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .sticky-nav a {
        display: block;
        padding: 10px 12px;
        color: #495057;
        text-decoration: none;
        border-radius: 8px;
        transition: all 0.2s;
        font-size: 14px;
      }

      .sticky-nav a:hover {
        background: #f8f9fa;
        color: #E5332A;
        transform: translateX(4px);
      }

      /* Main Content */
      .main-content {
        min-width: 0;
      }

      .content-section {
        margin-bottom: 48px;
      }

      .content-section h2 {
        font-size: 32px;
        font-weight: 800;
        margin-bottom: 24px;
        color: #2B2B2B;
        padding-top: 80px;
        margin-top: -80px;
      }

      .content-section h3 {
        font-size: 24px;
        font-weight: 700;
        margin: 32px 0 16px;
        color: #2B2B2B;
      }

      .content-section h4 {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 12px;
        color: #2B2B2B;
      }

      .content-box {
        background: #fff;
        padding: 32px;
        border-radius: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      }

      .content-box p {
        color: #495057;
        line-height: 1.8;
        margin-bottom: 16px;
      }

      .content-box a {
        color: #E5332A;
        text-decoration: none;
        font-weight: 600;
      }

      .content-box a:hover {
        text-decoration: underline;
      }

      .info-list {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin: 16px 0;
      }

      .info-list li {
        padding-left: 24px;
        position: relative;
        color: #495057;
        line-height: 1.7;
      }

      .info-list li::before {
        content: '•';
        position: absolute;
        left: 0;
        color: #E5332A;
        font-weight: 700;
        font-size: 20px;
      }

      /* Data Categories */
      .data-categories {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 24px;
        margin: 24px 0;
      }

      .category-card {
        padding: 24px;
        background: #f8f9fa;
        border-radius: 12px;
        border-left: 4px solid #E5332A;
      }

      .category-card svg {
        color: #E5332A;
        margin-bottom: 12px;
      }

      .category-card h4 {
        margin-bottom: 12px;
      }

      .category-card ul {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .category-card li {
        color: #495057;
        font-size: 14px;
      }

      /* Usage Grid */
      .usage-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        margin: 24px 0;
      }

      .usage-item {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .usage-number {
        width: 40px;
        height: 40px;
        background: linear-gradient(135deg, #E5332A, #ff4436);
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        flex-shrink: 0;
      }

      .usage-item h4 {
        margin-bottom: 4px;
        font-size: 16px;
      }

      .usage-item p {
        color: #495057;
        font-size: 14px;
        line-height: 1.6;
        margin: 0;
      }

      /* Sharing List */
      .sharing-list {
        display: grid;
        gap: 16px;
        margin: 24px 0;
      }

      .sharing-item {
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .sharing-item h4 {
        margin-bottom: 8px;
        color: #06d6a0;
      }

      .sharing-item p {
        margin: 0;
        font-size: 14px;
      }

      .important-note {
        display: flex;
        gap: 12px;
        padding: 20px;
        background: #fff5f5;
        border: 2px solid #E5332A;
        border-radius: 12px;
        margin-top: 24px;
      }

      .important-note svg {
        color: #E5332A;
        flex-shrink: 0;
      }

      .important-note p {
        margin: 0;
      }

      /* Security Measures */
      .security-measures {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 24px;
        margin: 24px 0;
      }

      .measure {
        padding: 24px;
        background: #f8f9fa;
        border-radius: 12px;
        text-align: center;
      }

      .measure svg {
        color: #E5332A;
        margin-bottom: 12px;
      }

      .measure h4 {
        margin-bottom: 8px;
      }

      .measure p {
        margin: 0;
        font-size: 14px;
      }

      /* Rights Grid */
      .rights-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        margin: 24px 0;
      }

      .right-card {
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
        border-top: 4px solid #E5332A;
      }

      .right-card h4 {
        margin-bottom: 8px;
        font-size: 16px;
      }

      .right-card p {
        margin: 0;
        font-size: 14px;
      }

      .rights-procedure {
        margin-top: 32px;
        padding: 24px;
        background: #eff6ff;
        border-left: 4px solid #3b82f6;
        border-radius: 8px;
      }

      .rights-procedure h4 {
        margin-bottom: 12px;
      }

      .rights-procedure p {
        margin: 0;
      }

      /* Contact Info */
      .contact-info {
        display: flex;
        flex-direction: column;
        gap: 20px;
        margin-top: 24px;
      }

      .contact-item {
        display: flex;
        gap: 16px;
        padding: 20px;
        background: #f8f9fa;
        border-radius: 12px;
      }

      .contact-item svg {
        color: #E5332A;
        flex-shrink: 0;
      }

      .contact-item strong {
        display: block;
        margin-bottom: 4px;
        color: #2B2B2B;
      }

      .contact-item a {
        color: #E5332A;
      }

      .contact-item p {
        margin: 0;
        color: #495057;
      }

      /* Responsive */
      @media (max-width: 992px) {
        .content-layout {
          grid-template-columns: 1fr;
        }

        .sidebar {
          order: 2;
        }

        .sticky-nav {
          position: static;
        }

        .data-categories {
          grid-template-columns: 1fr;
        }

        .usage-grid {
          grid-template-columns: 1fr;
        }

        .security-measures {
          grid-template-columns: 1fr;
        }

        .rights-grid {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 768px) {
        .page-hero h1 {
          font-size: 32px;
        }

        .content-section h2 {
          font-size: 24px;
        }

        .content-box {
          padding: 24px;
        }
      }
    `}</style>
  );
}